import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  useTheme,
  Skeleton,
  Alert,
  CircularProgress,
} from '@mui/material';
import ButtonMAIQ from 'app/components/ReusableUIComponents/LowLevelComponents/buttons/ButtonMAIQ';
import ModalMAIQ from 'app/components/ModalMAIQ';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import ConfirmationModal from 'app/components/ConfirmationModal';
import { formatDateForDisplay } from 'utils/dateUtils';
import FooterImageForm, { FooterImageFormData, FooterImageFormErrors } from './components/FooterImageForm';

interface FooterImageDetailsModalProps {
  open: boolean;
  onClose: () => void;
  data: any;
  onSave: (payload: any) => void;
  onDelete: () => void;
}

const FooterImageDetailsModal: React.FC<FooterImageDetailsModalProps> = ({
  open,
  onClose,
  data,
  onSave,
  onDelete,
}) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [formErrors, setFormErrors] = React.useState<FooterImageFormErrors>({});
  const [apiError, setApiError] = React.useState<string>('');
  const [formData, setFormData] = React.useState<FooterImageFormData>({
    image_description: '',
    image_address: '',
    image_url: '',
    expiry_date: '',
  });

  React.useEffect(() => {
    if (data) {
      setFormData({
        image_description: data.image_description || '',
        image_address: data.image_address || '',
        image_url: data.image_url || '',
        expiry_date: data.expiry_date || '',
      });
    }
    setFormErrors({});
    setApiError('');
  }, [data]);

  React.useEffect(() => {
    if (!isEditing) {
      setFormErrors({});
      setApiError('');
    }
  }, [isEditing]);

  React.useEffect(() => {
    if (!open) {
      setIsEditing(false);
      setFormErrors({});
      setApiError('');
    }
  }, [open]);

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field-specific error when user starts typing
    if (formErrors[field as keyof FooterImageFormErrors]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field as keyof FooterImageFormErrors];
        return newErrors;
      });
    }
    if (apiError) {
      setApiError('');
    }
  };

  const validateForm = (): boolean => {
    const errors: FooterImageFormErrors = {};

    if (!formData.image_description?.trim()) {
      errors.image_description = 'Image description is required';
    }
    if (!formData.image_address?.trim()) {
      errors.image_address = 'Image address is required';
    }
    if (!formData.image_url?.trim()) {
      errors.image_url = 'Landing page URL is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveClick = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setApiError('');
    try {
      await onSave(formData);
      setIsEditing(false);
      setFormErrors({});
      onClose(); // Close modal after successful save
    } catch (error) {
      setApiError(
        error instanceof Error ? error.message : 'Failed to save footer image',
      );
    } finally {
      setSaving(false);
    }
  };

  // Helper function to check if value should be displayed
  const shouldDisplayValue = (value: any): boolean => {
    if (value === null || value === undefined || value === '') return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    if (typeof value === 'number' && value === 0) return true; // Allow 0 for numbers
    return true;
  };

  if (!data) return null;

  return (
    <ModalMAIQ open={open} onClose={onClose} hideDefaultCloseButton={true}>
      <Paper
        elevation={0}
        sx={{ width: '600px', maxWidth: '90vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <Box
          className="modal-header"
          sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="h6" fontWeight={600}>
            Footer Image Details
          </Typography>
          <Box>
            <Tooltip title="Edit">
              <IconButton
                className="modal-header-button"
                size="small"
                onClick={() => setIsEditing(true)}
                disabled={isEditing}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton className="modal-header-button" size="small" sx={{ ml: 1 }} onClick={() => setOpenDeleteConfirm(true)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton className="modal-header-button" size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, overflowY: 'auto' }}>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}
          {isEditing ? (
            <FooterImageForm
              formData={formData}
              onFormChange={handleFormChange}
              errors={formErrors}
              disabled={saving}
              showImagePreview={true}
            />
          ) : (
            <>
              {/* Description */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Image Description
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {data.image_description}
                </Typography>
              </Box>

              {/* Image Preview */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Image Preview
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <img
                    src={data.image_address}
                    alt={data.image_description}
                    style={{ maxWidth: '100%', borderRadius: 4 }}
                  />
                </Box>
              </Box>

              {/* Image Address */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Image Address
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {data.image_address}
                </Typography>
              </Box>

              {/* Landing Page URL */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Landing Page URL
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {data.image_url}
                </Typography>
              </Box>

              {/* Expiry Date */}
              {shouldDisplayValue(data.expiry_date) && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Expiry Date
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {formatDateForDisplay(data.expiry_date) || '—'}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
        <Divider />

        {/* Footer action bar */}
        {isEditing && (
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: theme.palette.background.paper,
              borderTop: `1px solid ${theme.palette.divider}`,
              zIndex: 9,
              flexShrink: 0,
              gap: 2,
            }}
          >
            <ButtonMAIQ
              variant="outlined"
              onClick={() => {
                setIsEditing(false);
                setFormData({
                  image_description: data.image_description || '',
                  image_address: data.image_address || '',
                  image_url: data.image_url || '',
                  expiry_date: data.expiry_date || '',
                });
                setFormErrors({});
                setApiError('');
              }}
            >
              Cancel
            </ButtonMAIQ>
            {saving ? (
              <Skeleton
                variant="rectangular"
                width={140}
                height={36}
                sx={{
                  borderRadius: 1,
                  bgcolor: theme.palette.action.hover,
                }}
              />
            ) : (
              <ButtonMAIQ
                variant="contained"
                color="primary"
                onClick={handleSaveClick}
                startIcon={
                  saving ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
              >
                Save Changes
              </ButtonMAIQ>
            )}
          </Box>
        )}
      </Paper>
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={openDeleteConfirm}
        onClose={() => setOpenDeleteConfirm(false)}
        onConfirm={async () => {
          setIsDeleting(true);
          try {
            await onDelete();
            setOpenDeleteConfirm(false); // Close confirmation modal after successful deletion
          } catch (error) {
            // Error is handled by parent component, but keep modal open if there's an error
            console.error('Error deleting footer image:', error);
          } finally {
            setIsDeleting(false);
          }
        }}
        message="Are you sure you want to delete this footer image?"
        loading={isDeleting}
      />
    </ModalMAIQ>
  );
};

export default FooterImageDetailsModal; 